import { useEffect, useState } from 'react';
import {
  listOffers,
  createOffer,
  updateOffer,
  deleteOffer,
  uploadAdminImage,
} from '../../api/adminPackages';
import Button from '../../components/shared/ui/Button';
import { cx } from '../../components/shared/ui/utils';

const emptyForm = {
  id: null,
  title: '',
  subtitle: '',
  discountText: '',
  ctaText: '',
  ctaLink: '',
  imageUrl: '',
  startsAt: '',
  endsAt: '',
  isActive: true,
};

const AdminPackageOffers = () => {
  const [items, setItems] = useState([]);
  const [form, setForm] = useState(emptyForm);
  const [error, setError] = useState('');

  const load = async () => {
    setError('');
    try {
      const res = await listOffers();
      setItems(res?.data || res || []);
    } catch (err) {
      setError(err.message || 'Failed to load offers');
    }
  };

  useEffect(() => {
    load();
  }, []);

  const selectItem = (item) => {
    setForm({
      id: item.id,
      title: item.title || '',
      subtitle: item.subtitle || '',
      discountText: item.discount_text || '',
      ctaText: item.cta_text || '',
      ctaLink: item.cta_link || '',
      imageUrl: item.image_url || '',
      startsAt: item.starts_at || '',
      endsAt: item.ends_at || '',
      isActive: item.is_active !== false,
    });
  };

  const handleSave = async () => {
    setError('');
    try {
      const payload = {
        title: form.title,
        subtitle: form.subtitle,
        discount_text: form.discountText,
        cta_text: form.ctaText,
        cta_link: form.ctaLink,
        image_url: form.imageUrl,
        starts_at: form.startsAt,
        ends_at: form.endsAt,
        is_active: form.isActive,
      };
      if (form.id) await updateOffer(form.id, payload);
      else await createOffer(payload);
      setForm(emptyForm);
      await load();
    } catch (err) {
      setError(err.message || 'Save failed');
    }
  };

  const handleDelete = async () => {
    if (!form.id) return;
    if (!window.confirm('Delete this offer?')) return;
    try {
      await deleteOffer(form.id);
      setForm(emptyForm);
      await load();
    } catch (err) {
      setError(err.message || 'Delete failed');
    }
  };

  const handleUpload = async (event) => {
    const file = event.target.files?.[0];
    if (!file) return;
    try {
      const res = await uploadAdminImage(file);
      setForm((prev) => ({ ...prev, imageUrl: res.path }));
    } catch (err) {
      setError(err.message || 'Upload failed');
    }
  };

  return (
    <div className="space-y-4">
      <div className="flex items-center justify-between">
        <div>
          <p className="text-xs uppercase tracking-[0.2em] text-slate-500">Tours & Packages</p>
          <h1 className="text-xl font-semibold text-slate-900">Offers</h1>
        </div>
        <div className="flex gap-2">
          <Button variant="outline" onClick={() => setForm(emptyForm)}>
            New offer
          </Button>
          <Button onClick={handleSave}>Save</Button>
        </div>
      </div>
      {error && <div className="rounded-2xl border border-rose-100 bg-rose-50 p-3 text-sm text-rose-700">{error}</div>}
      <div className="grid gap-6 lg:grid-cols-[320px_1fr]">
        <div className="rounded-2xl border border-slate-200 bg-white p-3 space-y-2">
          {items.map((item) => (
            <button
              key={item.id}
              type="button"
              onClick={() => selectItem(item)}
              className={cx(
                'flex w-full flex-col rounded-xl px-3 py-2 text-left text-sm',
                form.id === item.id ? 'bg-slate-100' : 'hover:bg-slate-50'
              )}
            >
              <span className="font-semibold text-slate-900">{item.title}</span>
              <span className="text-xs text-slate-500">{item.discount_text}</span>
            </button>
          ))}
        </div>
        <div className="rounded-3xl border border-slate-200 bg-white p-5 space-y-3">
          <input
            className="w-full rounded-2xl border border-slate-200 px-3 py-2 text-sm"
            placeholder="Title"
            value={form.title}
            onChange={(e) => setForm((prev) => ({ ...prev, title: e.target.value }))}
          />
          <input
            className="w-full rounded-2xl border border-slate-200 px-3 py-2 text-sm"
            placeholder="Subtitle"
            value={form.subtitle}
            onChange={(e) => setForm((prev) => ({ ...prev, subtitle: e.target.value }))}
          />
          <input
            className="w-full rounded-2xl border border-slate-200 px-3 py-2 text-sm"
            placeholder="Discount text"
            value={form.discountText}
            onChange={(e) => setForm((prev) => ({ ...prev, discountText: e.target.value }))}
          />
          <div className="grid gap-2 md:grid-cols-2">
            <input
              className="w-full rounded-2xl border border-slate-200 px-3 py-2 text-sm"
              placeholder="CTA text"
              value={form.ctaText}
              onChange={(e) => setForm((prev) => ({ ...prev, ctaText: e.target.value }))}
            />
            <input
              className="w-full rounded-2xl border border-slate-200 px-3 py-2 text-sm"
              placeholder="CTA link"
              value={form.ctaLink}
              onChange={(e) => setForm((prev) => ({ ...prev, ctaLink: e.target.value }))}
            />
          </div>
          <div className="grid gap-2 md:grid-cols-2">
            <input
              className="w-full rounded-2xl border border-slate-200 px-3 py-2 text-sm"
              placeholder="Start date"
              value={form.startsAt}
              onChange={(e) => setForm((prev) => ({ ...prev, startsAt: e.target.value }))}
            />
            <input
              className="w-full rounded-2xl border border-slate-200 px-3 py-2 text-sm"
              placeholder="End date"
              value={form.endsAt}
              onChange={(e) => setForm((prev) => ({ ...prev, endsAt: e.target.value }))}
            />
          </div>
          <div className="flex items-center gap-3 text-sm">
            <input type="file" onChange={handleUpload} />
            {form.imageUrl && <span className="text-xs text-slate-500">Image selected</span>}
          </div>
          <label className="flex items-center gap-2 text-sm">
            <input
              type="checkbox"
              checked={form.isActive}
              onChange={(e) => setForm((prev) => ({ ...prev, isActive: e.target.checked }))}
            />
            Active
          </label>
          <div className="flex justify-end gap-2">
            {form.id && (
              <Button variant="outline" onClick={handleDelete}>
                Delete
              </Button>
            )}
            <Button onClick={handleSave}>Save offer</Button>
          </div>
        </div>
      </div>
    </div>
  );
};

export default AdminPackageOffers;
